/**
 * The widgets a custom confirmation page can place with block tags: `{{confirmation_card}}`,
 * `{{calendar_buttons}}`, `{{join_button}}` and `{{countdown}}`. Each is mounted in its own shadow
 * root (so the page's CSS and ours never collide) and reads the registration through the token on
 * the surrounding `[data-ffs-page]` root.
 */
import type { RegistrationDetails } from '@ffs/shared';
import { useEffect, useState } from 'preact/hooks';
import { getRegistration } from '../lib/api';
import { Notice, Shell } from './App';
import { CalendarButtons, Confirmation, JoinButton } from './Confirmation';

const FALLBACK = 'Your confirmation and join link are in your email.';

function useDetails(base: string, token: string | null): RegistrationDetails | null {
  const [details, setDetails] = useState<RegistrationDetails | null>(null);
  useEffect(() => {
    if (!token) return;
    let alive = true;
    getRegistration(base, token)
      .then((d) => alive && setDetails(d))
      .catch(() => alive && setDetails(null));
    return () => {
      alive = false;
    };
  }, [base, token]);
  return details;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Days / hours / minutes / seconds until `startAt`; "Starting now" once it has passed. */
export function Countdown({ startAt }: { startAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const left = Math.max(0, Math.floor((startAt - now) / 1000));
  if (left === 0)
    return (
      <p class="countdown-now" role="status">
        Starting now
      </p>
    );
  const days = Math.floor(left / 86_400);
  const hours = Math.floor((left % 86_400) / 3600);
  const minutes = Math.floor((left % 3600) / 60);
  const seconds = left % 60;
  const parts: Array<[string, string]> = [];
  if (days > 0) parts.push([String(days), days === 1 ? 'day' : 'days']);
  parts.push([pad(hours), 'hours'], [pad(minutes), 'minutes'], [pad(seconds), 'seconds']);
  return (
    <div class="countdown" role="timer" aria-live="off">
      {parts.map(([value, label]) => (
        <div class="countdown-part" key={label}>
          <b>{value}</b>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

export function BlockApp({
  base,
  name,
  token,
}: {
  base: string;
  name: string;
  token: string | null;
}) {
  const details = useDetails(base, token);
  const t = token ? encodeURIComponent(token) : null;
  const joinUrl = details?.join_url ?? (t ? `${base}/j/${t}` : null);
  const calendar = details?.calendar ?? (t ? { ics: `${base}/cal/${t}.ics` } : null);

  if (name === 'confirmation_card') {
    // The one block that is a card of its own: on a custom page there is no card around it.
    return (
      <Shell accent={null} hosted={false}>
        {token ? (
          <Confirmation
            title="You're in"
            when={details?.session_start_label ?? null}
            subtitle="A confirmation with your join link is on its way to your inbox."
            joinUrl={joinUrl}
            calendar={calendar}
          />
        ) : (
          <Notice>{FALLBACK}</Notice>
        )}
      </Shell>
    );
  }
  if (!token) return null;
  let body = null;
  if (name === 'calendar_buttons') body = <CalendarButtons cal={calendar} />;
  else if (name === 'join_button') body = joinUrl ? <JoinButton href={joinUrl} /> : null;
  else if (name === 'countdown')
    body = details ? <Countdown startAt={details.session_start_at} /> : null;
  if (!body) return null;
  return (
    <Shell accent={null} hosted>
      <div class="done">{body}</div>
    </Shell>
  );
}
